import React, { createContext, useContext, useEffect, useState } from "react";
import { useUser } from "./auth";
import { getMyModulesOnce } from "../utils/myModulesClient";

const PermissionContext = createContext(null);

const ACTION_ALIAS_MAP = {
  readAny: "read",
  readOwn: "read",
  createAny: "create",
  createOwn: "create",
  updateAny: "update",
  updateOwn: "update",
  deleteAny: "delete",
  deleteOwn: "delete",
};

const toAction = (action) => ACTION_ALIAS_MAP[action] || action;

const normalizeActions = (actions) =>
  Array.from(
    new Set(
      (Array.isArray(actions) ? actions : [])
        .map((action) => toAction(String(action || "").trim()))
        .filter(Boolean),
    ),
  );

const normalizeFieldList = (fields) =>
  Array.from(
    new Set(
      (Array.isArray(fields) ? fields : [])
        .map((field) => {
          const value = String(field || "").trim();
          const lowered = value.toLowerCase();
          if (
            lowered === "*" ||
            lowered === "all_fields" ||
            lowered === "all fields" ||
            lowered === "allfields"
          ) {
            return "*";
          }
          return value;
        })
        .filter(Boolean),
    ),
  );

const normalizeFieldKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const fieldKeyAliases = (fieldName) => {
  const normalized = normalizeFieldKey(fieldName);
  const aliases = new Set([normalized]);
  if (normalized.endsWith("s")) {
    aliases.add(normalized.slice(0, -1));
  } else if (normalized) {
    aliases.add(`${normalized}s`);
  }
  return aliases;
};

const hasWildcardAccess = (fields) => {
  return (Array.isArray(fields) ? fields : []).some((field) => {
    const value = String(field || "").trim().toLowerCase();
    return (
      value === "*" ||
      value === "all_fields" ||
      value === "all fields" ||
      value === "allfields"
    );
  });
};

const normalizeModuleFields = (fields) =>
  (Array.isArray(fields) ? fields : [])
    .map((field) => {
      if (typeof field === "string") {
        return { fieldName: field, label: field, type: "text" };
      }
      if (!field || typeof field !== "object") return null;
      const fieldName = String(field.fieldName || field.name || "").trim();
      if (!fieldName) return null;
      return {
        fieldName,
        label: String(field.label || fieldName),
        type: String(field.type || "text"),
      };
    })
    .filter(Boolean);

const normalizeSubModules = (subModules) =>
  (Array.isArray(subModules) ? subModules : [])
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const id = String(item.id || item._id || "").trim();
      const label = String(item.label || "").trim();
      const path = String(item.path || item.route || "").trim();
      if (!id && !label && !path) return null;
      return {
        id: id || label || path,
        label: label || path || id,
        path,
        route: path,
        icon: item.icon || "",
        order: Number(item.order || 0),
        isActive: item.isActive !== false,
        resource: String(item.resource || "").trim(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

const DEFAULT_MODULE_ROUTES = {
  dashboard: "/dashboard",
  registrations: "/registrations",
  registration: "/registrations",
  member_registrations: "/member-registrations",
  memberregistrations: "/member-registrations",
  company_profiles: "/company-profile",
  companyprofiles: "/company-profile",
  company_profile: "/company-profile",
  companyprofile: "/company-profile",
  profiles: "/company-profile",
  renewals: "/renewals",
  renewal: "/renewals",
  member_renewals: "/member-renewals",
  memberrenewals: "/member-renewals",
  mvds: "/mvd",
  mvd: "/mvd",
  proof_of_payments: "/proof-of-payments",
  proof_of_payment: "/proof-of-payments",
  proofofpayments: "/proof-of-payments",
  payments: "/payments",
  payment: "/payments",
  change_requests: "/change-requests",
  changerequests: "/change-requests",
  quarterly_compliance: "/quarterly-compliance",
  quarterlycompliance: "/quarterly-compliance",
  notifications: "/notifications",
  notification: "/notifications",
  reports: "/reports",
  reporting: "/reports",
  system_settings: "/settings",
  systemsettings: "/settings",
  account_settings: "/account-settings",
  accountsettings: "/account-settings",
};

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const resolveModuleRoute = (moduleItem, name) => {
  const explicitRoute = String(moduleItem?.route || moduleItem?.path || "").trim();
  if (explicitRoute) return explicitRoute;

  const candidates = [
    name,
    moduleItem?.name,
    moduleItem?.resource,
    moduleItem?.label,
    moduleItem?.id,
    moduleItem?.moduleId,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeKey(candidate);
    const compact = normalized.replace(/_/g, "");
    if (DEFAULT_MODULE_ROUTES[normalized]) return DEFAULT_MODULE_ROUTES[normalized];
    if (DEFAULT_MODULE_ROUTES[compact]) return DEFAULT_MODULE_ROUTES[compact];
  }

  return "";
};

const toModuleName = (moduleItem, index) => {
  const raw =
    moduleItem?.name ||
    moduleItem?.resource ||
    moduleItem?.label ||
    moduleItem?.moduleId ||
    moduleItem?.id ||
    `module_${index}`;

  return normalizeKey(raw);
};

const derivePermissionFromActionObjects = (actions) => {
  const allowedActions = new Set();
  const viewableFields = new Set();
  const editableFields = new Set();

  (Array.isArray(actions) ? actions : []).forEach((action) => {
    const actionName = String(action?.name || "").toLowerCase();
    const attrs = Array.isArray(action?.attributes) ? action.attributes : [];

    if (actionName.startsWith("read")) {
      allowedActions.add("read");
      attrs.forEach((attr) => viewableFields.add(attr));
    } else if (actionName.startsWith("create")) {
      allowedActions.add("create");
      attrs.forEach((attr) => editableFields.add(attr));
    } else if (actionName.startsWith("update")) {
      allowedActions.add("update");
      attrs.forEach((attr) => editableFields.add(attr));
    } else if (actionName.startsWith("delete")) {
      allowedActions.add("delete");
    }
  });

  return {
    allowedActions: Array.from(allowedActions),
    viewableFields: Array.from(viewableFields),
    editableFields: Array.from(editableFields),
  };
};

const normalizeModules = (modules) => {
  return (Array.isArray(modules) ? modules : [])
    .map((moduleItem, index) => {
      if (!moduleItem || typeof moduleItem !== "object") return null;

      const name = toModuleName(moduleItem, index);
      if (!name) return null;

      const id = String(
        moduleItem.id ||
          moduleItem._id ||
          moduleItem.moduleId ||
          name ||
          `module-${index}`,
      );
      const route = resolveModuleRoute(moduleItem, name);
      const derived = derivePermissionFromActionObjects(moduleItem.actions);
      const normalizedActions = normalizeActions(
        derived.allowedActions.length > 0
          ? derived.allowedActions
          : moduleItem.allowedActions,
      );
      const normalizedViewFields = normalizeFieldList(
        derived.viewableFields.length > 0
          ? derived.viewableFields
          : moduleItem.viewableFields,
      );
      const normalizedEditFields = normalizeFieldList(
        derived.editableFields.length > 0
          ? derived.editableFields
          : moduleItem.editableFields,
      );

      return {
        ...moduleItem,
        id,
        moduleId: id,
        name,
        label: String(moduleItem.label || name),
        route,
        resource: String(moduleItem.resource || name),
        order: Number(moduleItem.order || 0),
        actions: Array.isArray(moduleItem.actions) ? moduleItem.actions : [],
        allowedActions: normalizedActions,
        viewableFields: normalizedViewFields,
        editableFields: normalizedEditFields,
        fields: normalizeModuleFields(moduleItem.fields),
        subModules: normalizeSubModules(moduleItem.subModules),
        hasSubModules: Boolean(moduleItem.hasSubModules),
      };
    })
    .filter(Boolean);
};

const extractModulesPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.modules)) return payload.modules;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const extractStandaloneResourcesPayload = (payload) => {
  if (Array.isArray(payload?.standaloneResources)) {
    return payload.standaloneResources;
  }
  if (Array.isArray(payload?.data?.standaloneResources)) {
    return payload.data.standaloneResources;
  }
  return [];
};

const setPermissionAliases = (permissionMap, keys, value, options = {}) => {
  const { overwrite = false } = options;
  keys
    .map((key) => String(key || "").trim())
    .filter(Boolean)
    .forEach((key) => {
      const rawKey = key;
      const loweredKey = key.toLowerCase();
      if (overwrite || !permissionMap[rawKey]) {
        permissionMap[rawKey] = value;
      }
      if (overwrite || !permissionMap[loweredKey]) {
        permissionMap[loweredKey] = value;
      }
    });
};

const buildAliasCandidates = (...values) => {
  const aliases = new Set();
  values
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .forEach((value) => {
      const lowered = value.toLowerCase();
      const normalized = lowered
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      const compact = lowered.replace(/[^a-z0-9]/g, "");

      const routeTail = lowered.startsWith("/")
        ? lowered.split("/").filter(Boolean).pop()
        : "";
      const routeTailNormalized = String(routeTail || "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

      [value, lowered, normalized, compact, routeTail, routeTailNormalized]
        .filter(Boolean)
        .forEach((alias) => aliases.add(alias));

      if (normalized.endsWith("_management")) {
        const base = normalized.replace(/_management$/, "");
        aliases.add(base);
        aliases.add(base.endsWith("s") ? base.slice(0, -1) : `${base}s`);
      }

      if (normalized.endsWith("s")) {
        aliases.add(normalized.slice(0, -1));
      } else if (normalized) {
        aliases.add(`${normalized}s`);
      }
    });
  return Array.from(aliases);
};

const buildPermissionsMap = (modules) => {
  return modules.reduce((acc, moduleItem) => {
    const permissionValue = {
      actions: normalizeActions(moduleItem.allowedActions),
      viewableFields: normalizeFieldList(moduleItem.viewableFields),
      editableFields: normalizeFieldList(moduleItem.editableFields),
      fields: normalizeModuleFields(moduleItem.fields),
      actionDefinitions: Array.isArray(moduleItem.actions)
        ? moduleItem.actions
        : [],
      subModules: normalizeSubModules(moduleItem.subModules),
      hasSubModules: Boolean(moduleItem.hasSubModules),
      moduleId: moduleItem.id,
      name: moduleItem.name,
      resource: moduleItem.resource,
    };

    setPermissionAliases(
      acc,
      buildAliasCandidates(
        moduleItem.name,
        moduleItem.label,
        moduleItem.resource,
        moduleItem.id,
        moduleItem.route,
      ),
      permissionValue,
    );

    // Sub-modules inherit the parent module action scope for frontend UX checks.
    // API endpoints still enforce actual authorization on the backend.
    normalizeSubModules(moduleItem.subModules).forEach((subModule) => {
      setPermissionAliases(
        acc,
        buildAliasCandidates(
          subModule.id,
          subModule.label,
          subModule.resource,
          subModule.path,
          subModule.route,
        ),
        permissionValue,
      );
    });

    return acc;
  }, {});
};

const resolveModulePermission = (permissions, moduleName) => {
  if (!moduleName) return null;
  const raw = String(moduleName).trim();
  const lowered = raw.toLowerCase();
  const normalized = lowered
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const compact = lowered.replace(/[^a-z0-9]/g, "");
  const singular = normalized.endsWith("s")
    ? normalized.slice(0, -1)
    : normalized;
  const plural = normalized.endsWith("s") ? normalized : `${normalized}s`;

  return (
    permissions[raw] ||
    permissions[lowered] ||
    permissions[normalized] ||
    permissions[compact] ||
    permissions[singular] ||
    permissions[plural] ||
    null
  );
};

export const PermissionProvider = ({ children }) => {
  const { isUserAuthenticated } = useUser();
  const [accessibleModules, setAccessibleModules] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [hasLoadedPermissions, setHasLoadedPermissions] = useState(false);

  useEffect(() => {
    const fetchModules = async () => {
      if (!isUserAuthenticated) {
        setAccessibleModules([]);
        setPermissions({});
        setHasLoadedPermissions(false);
        setIsLoadingPermissions(false);
        return;
      }

      setIsLoadingPermissions(true);
      setHasLoadedPermissions(false);
      try {
        const payload = await getMyModulesOnce();
        const modules = normalizeModules(extractModulesPayload(payload));
        const standaloneResources = normalizeModules(
          extractStandaloneResourcesPayload(payload),
        );
        const permissionSources = [...modules, ...standaloneResources];

        setAccessibleModules(modules);
        setPermissions(buildPermissionsMap(permissionSources));
      } catch (error) {
        console.error("Permission bootstrap failed:", error);
        setAccessibleModules([]);
        setPermissions({});
      } finally {
        setHasLoadedPermissions(true);
        setIsLoadingPermissions(false);
      }
    };

    fetchModules();
  }, [isUserAuthenticated]);

  const canDo = (moduleName, action) => {
    const modulePermission = resolveModulePermission(permissions, moduleName);
    if (!modulePermission) return false;
    const normalizedAction = toAction(action);
    if (modulePermission.actions.includes(normalizedAction)) return true;
    const rawDefinitions = Array.isArray(modulePermission.actionDefinitions)
      ? modulePermission.actionDefinitions
      : [];
    return rawDefinitions.some(
      (definition) =>
        String(definition?.name || "").trim() === String(action || "").trim(),
    );
  };

  const canDoAny = (moduleName, actions = []) => {
    const actionList = Array.isArray(actions) ? actions : [actions];
    return actionList.some((actionName) => canDo(moduleName, actionName));
  };

  const canDoExact = (moduleName, actionName) => {
    const modulePermission = resolveModulePermission(permissions, moduleName);
    if (!modulePermission) return false;
    const rawDefinitions = Array.isArray(modulePermission.actionDefinitions)
      ? modulePermission.actionDefinitions
      : [];
    return rawDefinitions.some(
      (definition) =>
        String(definition?.name || "").trim() === String(actionName || "").trim(),
    );
  };

  const getViewableFields = (moduleName) => {
    return (
      resolveModulePermission(permissions, moduleName)?.viewableFields || []
    );
  };

  const getEditableFields = (moduleName) => {
    return (
      resolveModulePermission(permissions, moduleName)?.editableFields || []
    );
  };

  const canViewField = (moduleName, fieldName) => {
    const modulePermission = resolveModulePermission(permissions, moduleName);
    if (!modulePermission) return false;
    if (modulePermission.viewableFields.length === 0)
      return canDo(moduleName, "read");
    if (hasWildcardAccess(modulePermission.viewableFields)) return true;
    const allowed = new Set(
      modulePermission.viewableFields.map((field) => normalizeFieldKey(field)),
    );
    return Array.from(fieldKeyAliases(fieldName)).some((alias) =>
      allowed.has(alias),
    );
  };

  const canEditField = (moduleName, fieldName) => {
    const modulePermission = resolveModulePermission(permissions, moduleName);
    if (!modulePermission) return false;
    if (modulePermission.editableFields.length === 0)
      return canDo(moduleName, "update") || canDo(moduleName, "create");
    if (hasWildcardAccess(modulePermission.editableFields)) return true;
    const allowed = new Set(
      modulePermission.editableFields.map((field) => normalizeFieldKey(field)),
    );
    return Array.from(fieldKeyAliases(fieldName)).some((alias) =>
      allowed.has(alias),
    );
  };

  const canTransition = (
    moduleNameOrNames,
    statusFields = ["status", "membership_status"],
  ) => {
    const moduleNames = Array.isArray(moduleNameOrNames)
      ? moduleNameOrNames
      : [moduleNameOrNames];
    const normalizedStatusFields = normalizeFieldList(statusFields);

    return moduleNames.some((moduleName) => {
      const modulePermission = resolveModulePermission(permissions, moduleName);
      if (!modulePermission) return false;

      const actionDefinitions = Array.isArray(
        modulePermission.actionDefinitions,
      )
        ? modulePermission.actionDefinitions
        : [];
      const updateDefinitions = actionDefinitions.filter((definition) =>
        String(definition?.name || "")
          .toLowerCase()
          .startsWith("update"),
      );
      const hasUpdateAction =
        modulePermission.actions.includes("update") ||
        updateDefinitions.length > 0;
      if (!hasUpdateAction) return false;

      const updateAttributes = normalizeFieldList(
        updateDefinitions.flatMap((definition) =>
          Array.isArray(definition?.attributes) ? definition.attributes : [],
        ),
      );

      // Backward compatibility: when update action exists but attributes are omitted,
      // treat it as transition-capable.
      if (updateAttributes.length === 0) return true;
      if (updateAttributes.includes("*")) return true;

      return normalizedStatusFields.some((field) =>
        updateAttributes.includes(field),
      );
    });
  };

  const getModuleFields = (moduleName) => {
    return resolveModulePermission(permissions, moduleName)?.fields || [];
  };

  const value = {
    accessibleModules,
    permissions,
    isLoadingPermissions:
      isLoadingPermissions || (isUserAuthenticated && !hasLoadedPermissions),
    canDo,
    canDoAny,
    canDoExact,
    getViewableFields,
    getEditableFields,
    canViewField,
    canEditField,
    canTransition,
    getModuleFields,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return ctx;
};
